/**
 * work_orders — every vertical. GET /dashboard/work-orders (api#1141): open
 * work orders per system — downhole, rental maintenance, midstream and
 * equipment service — each present only when the company has it.
 *
 * `overdue_count: null` MEANS THE SYSTEM HAS NO DUE DATE (rentals, midstream).
 * It renders —, never 0: "nothing overdue" would be a claim about due dates
 * that do not exist. A source that FAILED says so in place of its figures,
 * never zeros. Midstream has no screen: its rows and block carry no link and
 * render as plain text, never as a dead button.
 *
 * A null title means the API dropped one that only repeated the number
 * (api#1167: Smith's "Tong-02 · Tong-02"), so the row reads the number alone,
 * never "Tong-02 · —".
 */
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { WidgetPanel } from '../WidgetPanel';
import { useWorkOrders } from '../overviewQueries';
import { OpsBadge, OpsRow, OpsWidgetRead, dash } from '../OpsWidgetRead';
import { formatCalendarDate } from '../../../lib/calendar-date';
import type { WorkOrderBlock, WorkOrders } from '../../../types/overview-operations';

const SOURCE_TITLE: Record<string, string> = {
  downhole: 'Downhole work orders', rentals: 'Rental maintenance', midstream: 'Midstream work orders', equipment: 'Equipment service',
};

function Block({ b }: { b: WorkOrderBlock }) {
  const navigate = useNavigate();
  const title = b.status === 'failed' ? SOURCE_TITLE[b.source] ?? 'Work orders' : b.label || SOURCE_TITLE[b.source] || 'Work orders';
  return (
    <section data-testid={`wo-block-${b.source}`} className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-xs font-bold uppercase tracking-wide text-fieldiq-text">{title}</h3>
        {b.link && b.status !== 'failed' && (
          <button type="button" onClick={() => navigate(b.link!)} className="inline-flex items-center gap-0.5 text-xs font-semibold text-fieldiq-text underline decoration-fi-amber decoration-2 underline-offset-4">
            Open <ChevronRight className="size-3.5" aria-hidden="true" />
          </button>
        )}
      </div>
      {b.status === 'failed' ? (
        <p data-testid="wo-failed" className="text-xs text-fieldiq-muted">Couldn’t load these. This is not an empty list.</p>
      ) : (
        <>
          <p data-testid={`wo-figures-${b.source}`} className="text-xs text-fieldiq-muted">
            Open <span className="font-bold tabular-nums text-fieldiq-text">{b.open_count}</span>
            {' · '}Overdue <span className="font-bold tabular-nums text-fieldiq-text">{b.overdue_count === null ? '—' : b.overdue_count}</span>
          </p>
          {b.items.length === 0 ? (
            <p data-testid="wo-none" className="text-xs text-fieldiq-muted">None open.</p>
          ) : (
            <div className="divide-y divide-fieldiq-border rounded-fi-control border border-fieldiq-border">
              {b.items.map((i) => (
                <OpsRow key={i.id} to={i.link} onOpen={navigate} testId={`wo-item-${i.id}`}>
                  <span className="min-w-0 truncate font-semibold text-fieldiq-text">{dash(i.title) === '—' ? dash(i.number) : `${dash(i.number)} · ${i.title}`}</span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="tabular-nums text-fieldiq-muted">{formatCalendarDate(i.due_date, { month: 'short', day: 'numeric' }) ?? '—'}</span>
                    {i.overdue && <OpsBadge fill="bg-fi-signal-red" testId="wo-overdue-badge">Overdue</OpsBadge>}
                  </span>
                </OpsRow>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Body({ d }: { d: WorkOrders }) {
  if (d.sources.length === 0) return <p data-testid="wo-no-sources" className="text-sm text-fieldiq-muted">No work-order system on this account.</p>;
  return <div className="space-y-5">{d.sources.map((b) => <Block key={b.source} b={b} />)}</div>;
}

export default function WorkOrdersWidget() {
  const q = useWorkOrders();
  return (
    <WidgetPanel widgetKey="work_orders" eyebrow="Shop and service" title="Work Orders">
      <div className="p-4">
        <OpsWidgetRead q={q} subject="work orders" allowed="admins, office staff, supervisors, dispatchers and logistics">
          {(d) => <Body d={d} />}
        </OpsWidgetRead>
      </div>
    </WidgetPanel>
  );
}
